import React from 'react';
import PropTypes from 'prop-types';
import { translator } from 'mage-i18n';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import { IconError, Link, SpacerHorizontal, SpacerVertical } from '@klarna/bubble-ui';
import {
  Alert,
  analytics,
  defaultContainerPropTypes,
  HorizontalWrapper,
  moetPropTypes,
} from 'moet-core';
import { generateCyElemId } from 'moet-test-utils/cypress/utils';

import { CAPS } from '@/constants';
import { OPTIONS_META } from '@/constants/countries';
import { BackButton, CallToActionButton, ContainerTemplate, SwitchToggle } from '@/components';
import StakeholderFormCard from '@/components/StakeholderFormCard';
import {
  STAKEHOLDER_ROLES_V2,
  stakeholderUtils,
  withStakeholderLogic,
} from '@/containers/withStakeholderLogic';
import {
  getMerchantCountryKey,
  getMerchantAwareTranslator,
  getMerchantAwareArrayTranslator,
} from '@/selectors/merchant';
import { isUnmanagedKyc } from '@/selectors/widget';

import { CY_SOLE_TRADER_IDS } from '../constants';
import { ANALYTICS_EVENTS } from '../analyticsEvents';

const tPrefix = 'products-fe.containers.sole_trader';

export class SoleTrader extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      areYouTheSoleTrader: false,
      ctaClicked: false,
    };

    this.onNext = this.onNext.bind(this);
    this.getBody = this.getBody.bind(this);
    this.isFormValid = this.isFormValid.bind(this);
    this.onChangeAreYouTheSoleTrader = this.onChangeAreYouTheSoleTrader.bind(this);
  }

  getBody() {
    const { mid, stakeholders } = this.props;

    return {
      payload: {
        params: {
          mid,
        },
        body: stakeholderUtils.getStakeholdersMappedValues(stakeholders),
      },
    };
  }

  onNext() {
    const { onNextWrapper } = this.props;
    const { areYouTheSoleTrader = false } = this.state;

    this.setState({
      ctaClicked: !areYouTheSoleTrader && this.isFormValid(),
    });

    onNextWrapper(this.getBody(), this.isFormValid() && areYouTheSoleTrader);
  }

  isFormValid() {
    const { roleConstraints, stakeholders } = this.props;
    return (
      stakeholderUtils.isStakeholderConstraintsValid(stakeholders, roleConstraints) &&
      stakeholderUtils.isStakeholdersValid(stakeholders)
    );
  }

  onChangeAreYouTheSoleTrader(val) {
    this.setState({ areYouTheSoleTrader: val });
  }

  render() {
    const {
      hasBack,
      isDisabled,
      isLoading,
      onBack,
      t,
      tMerchantAware,
      tArrayMerchantAware,
      onChangeStakeholder,
      onTouchPersonalId,
      onBlurStakeholder,
      stakeholders,
    } = this.props;

    const { areYouTheSoleTrader = false, ctaClicked } = this.state;

    const shouldShowSoleTraderError = this.isFormValid() && !areYouTheSoleTrader && ctaClicked;

    return (
      <ContainerTemplate
        title={t(`${tPrefix}.title`)}
        subtitle={tArrayMerchantAware(`${tPrefix}.subtitle`).map(({ text, link }, index) =>
          link ? (
            <Link key={index} href={link} target="_blank">
              {text}
            </Link>
          ) : (
            <span key={index}>{text}</span>
          ),
        )}
        info={tMerchantAware(`${tPrefix}.infobox`)}
      >
        <SpacerVertical small />
        <StakeholderFormCard
          name="sole-trader"
          autoFocus
          tabIndexStart={1}
          stakeholder={stakeholders[0]}
          onBlur={key => onBlurStakeholder(0, key)}
          onChange={(key, value) => onChangeStakeholder(0, key, value)}
          onTouchPersonalId={() => onTouchPersonalId(0)}
          isDisabled={isDisabled}
        >
          <SpacerVertical extraExtraSmall />
          <SwitchToggle
            tabIndex={11}
            checked={areYouTheSoleTrader}
            disabled={isLoading}
            onChange={this.onChangeAreYouTheSoleTrader}
            messagePath={`${tPrefix}.fields.are_you_the_sole_trader.label`}
            idForTestSuite={CY_SOLE_TRADER_IDS.INPUT_SOLE_TRADER_APPLICANT_SWITCH}
            error={shouldShowSoleTraderError}
          />
          {shouldShowSoleTraderError && (
            <>
              <Alert
                type="error"
                content={t(`${tPrefix}.fields.are_you_the_sole_trader.error`)}
                icon={IconError}
              />
              <SpacerVertical extraSmall />
            </>
          )}
        </StakeholderFormCard>
        <SpacerVertical large />
        <HorizontalWrapper display="inline-flex" alignItems="center" justifyContent="flex-end">
          {hasBack && (
            <>
              <BackButton tabIndex={13} onClick={onBack} disabled={isDisabled}>
                {t(`${tPrefix}.buttons.back`)}
              </BackButton>
              <SpacerHorizontal medium />
            </>
          )}
          <CallToActionButton
            tabIndex={12}
            loading={isLoading}
            onClick={this.onNext}
            disabled={isDisabled}
            {...generateCyElemId(CY_SOLE_TRADER_IDS.BTN_SUBMIT, window)}
          >
            {t(`${tPrefix}.buttons.next`)}
          </CallToActionButton>
        </HorizontalWrapper>
      </ContainerTemplate>
    );
  }
}

SoleTrader.propTypes = {
  ...defaultContainerPropTypes,
  ...moetPropTypes.defaultStakeholderContainerPropTypes,
  forceCountry: PropTypes.string.isRequired,
  isUnmanagedKyc: PropTypes.bool.isRequired,
  mid: PropTypes.string.isRequired,
  t: PropTypes.func.isRequired,
  tArrayMerchantAware: PropTypes.func.isRequired,
  tMerchantAware: PropTypes.func.isRequired,
};

const WrappedComponent = compose(
  connect(state => ({
    t: translator(state),
    tMerchantAware: getMerchantAwareTranslator(state),
    tArrayMerchantAware: getMerchantAwareArrayTranslator(state),
    // FIXME: This true should be !isUnmanagedKyc
    forceCountry: true && getMerchantCountryKey(state),
    isUnmanagedKyc: isUnmanagedKyc(state),
  })),
  withStakeholderLogic(CAPS.V2_MERCHANTS_STAKEHOLDERS_PUT, {
    extraProps: {
      overrideStakeholders: true,
      defaultRole: STAKEHOLDER_ROLES_V2.SOLE_TRADER,
      roleConstraints: {
        [STAKEHOLDER_ROLES_V2.SOLE_TRADER]: {
          min: 1,
          max: 1,
        },
      },
    },
    capabilityCallback: () => analytics.logEvent(ANALYTICS_EVENTS.soleTrader),
  }),
)(SoleTrader);

WrappedComponent.onboardingTypes = '*';
WrappedComponent.variantId = OPTIONS_META.SOLE_TRADER_KIND.OPTIONS.WITHOUT_OPERATING_ADDRESS;

WrappedComponent.fieldsToFill = [
  {
    fieldName: 'stakeholders',
    roles: [STAKEHOLDER_ROLES_V2.SOLE_TRADER],
  },
];
export default WrappedComponent;
